import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faEdit,
  faTrash,
  faPlay,
  faPause,
  faHistory,
  faEye,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faPaperPlane,
  faCalendarAlt,
  faBell,
  faChartBar,
  faUsers,
  faSearch,
  faTimes,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import agendamentoWhatsAppService from '../../services/agendamentoWhatsAppService';
import usuarioService from '../../services/usuarioService';
import Toast from '../../components/Toast';

// Mapeamento de cores por status
const CORES_STATUS = {
  ativo: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle },
  pausado: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: faPause },
  inativo: { bg: 'bg-gray-100', text: 'text-gray-700', icon: faTimesCircle },
  erro: { bg: 'bg-red-100', text: 'text-red-700', icon: faExclamationTriangle }
};

// Nomes legíveis dos tipos de relatório
const NOMES_TIPOS_RELATORIO = {
  pedidos_pendentes: 'Pedidos Pendentes',
  resumo_diario: 'Resumo Diário',
  resumo_semanal: 'Resumo Semanal',
  resumo_mensal: 'Resumo Mensal',
  alertas_estoque: 'Alertas de Estoque',
  financeiro_dia: 'Financeiro do Dia',
  notas_lancadas: 'Notas Lançadas',
  matriculas_periodo: 'Matrículas do Período',
  personalizado: 'Personalizado'
};

// Nomes dos dias da semana
const NOMES_DIAS_SEMANA = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado'
};

/**
 * Formata telefone para padrão WhatsApp (apenas números com código do país)
 * @param {string} telefone - Telefone em qualquer formato
 * @returns {string} Telefone formatado (ex: 5521999999999)
 */
const formatarTelefoneWhatsApp = (telefone) => {
  if (!telefone) return '';
  
  // Remove tudo que não é número
  let numero = telefone.replace(/\D/g, '');
  
  // Se não começa com 55, adiciona código do Brasil
  if (!numero.startsWith('55')) {
    numero = '55' + numero;
  }
  
  return numero;
};

const AgendamentosWhatsAppPage = () => {
  useAuth();

  // States principais
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Filtros
  const [filtros, setFiltros] = useState({
    status: '',
    tipo_relatorio: '',
    search: ''
  });

  // Modal de formulário
  const [modalAberto, setModalAberto] = useState(false);
  const [agendamentoEditando, setAgendamentoEditando] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo_relatorio: 'resumo_diario',
    configuracao_relatorio: {},
    destinatario_usuario_id: '',
    destinatario_telefone: '',
    destinatario_nome: '',
    recorrencia: 'diaria',
    horario: '09:00',
    dia_semana: '',
    dia_mes: '',
    data_inicio: '',
    data_fim: ''
  });

  // Modal de histórico
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [historicoAgendamento, setHistoricoAgendamento] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);

  // Modal de preview
  const [modalPreviewAberto, setModalPreviewAberto] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Dados auxiliares
  const [tiposRelatorio, setTiposRelatorio] = useState([]);
  const [recorrencias, setRecorrencias] = useState([]);
  const [diasSemana, setDiasSemana] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [whatsappStatus, setWhatsappStatus] = useState(null);

  // Configurações específicas por tipo de relatório
  const [configPedidosPendentes, setConfigPedidosPendentes] = useState({
    dias_pendente: 5,
    data_referencia: '2025-12-01'
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Carregar dados auxiliares
  const carregarDadosAuxiliares = useCallback(async () => {
    try {
      const [tiposRes, recorrenciasRes, usuariosRes] = await Promise.all([
        agendamentoWhatsAppService.getTiposRelatorio(),
        agendamentoWhatsAppService.getRecorrencias(),
        usuarioService.listForSelect()
      ]);

      if (tiposRes.success) {
        setTiposRelatorio(tiposRes.data || []);
      }
      if (recorrenciasRes.success) {
        setRecorrencias(recorrenciasRes.data.recorrencias || []);
        setDiasSemana(recorrenciasRes.data.diasSemana || []);
      }
      if (usuariosRes.success) {
        setUsuarios(usuariosRes.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados auxiliares:', error);
    }
  }, []);

  // Verificar status do WhatsApp
  const verificarWhatsApp = useCallback(async () => {
    try {
      const response = await agendamentoWhatsAppService.verificarWhatsApp();
      setWhatsappStatus(response.data);
    } catch (error) {
      console.error('Erro ao verificar WhatsApp:', error);
      setWhatsappStatus({ connected: false, error: error.message });
    }
  }, []);

  // Carregar agendamentos
  const carregarAgendamentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filtros
      };

      // Remover filtros vazios
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await agendamentoWhatsAppService.listar(params);
      if (response.success) {
        setAgendamentos(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      showToast('Erro ao carregar agendamentos', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filtros]);

  useEffect(() => {
    carregarDadosAuxiliares();
    verificarWhatsApp();
  }, [carregarDadosAuxiliares, verificarWhatsApp]);

  useEffect(() => {
    carregarAgendamentos();
  }, [carregarAgendamentos]);

  // Abrir modal para criar
  const abrirModalCriar = () => {
    setAgendamentoEditando(null);
    setFormData({
      nome: '',
      descricao: '',
      tipo_relatorio: 'resumo_diario',
      configuracao_relatorio: {},
      destinatario_usuario_id: '',
      destinatario_telefone: '',
      destinatario_nome: '',
      recorrencia: 'diaria',
      horario: '09:00',
      dia_semana: '',
      dia_mes: '',
      data_inicio: '',
      data_fim: ''
    });
    setConfigPedidosPendentes({
      dias_pendente: 5,
      data_referencia: '2025-12-01'
    });
    setModalAberto(true);
  };

  // Abrir modal para editar
  const abrirModalEditar = (agendamento) => {
    setAgendamentoEditando(agendamento);
    setFormData({
      nome: agendamento.nome || '',
      descricao: agendamento.descricao || '',
      tipo_relatorio: agendamento.tipo_relatorio || 'resumo_diario',
      configuracao_relatorio: agendamento.configuracao_relatorio || {},
      destinatario_usuario_id: agendamento.destinatario_usuario_id || '',
      destinatario_telefone: agendamento.destinatario_telefone || '',
      destinatario_nome: agendamento.destinatario_nome || '',
      recorrencia: agendamento.recorrencia || 'diaria',
      horario: agendamento.horario?.substring(0, 5) || '09:00',
      dia_semana: agendamento.dia_semana ?? '',
      dia_mes: agendamento.dia_mes || '',
      data_inicio: agendamento.data_inicio?.split('T')[0] || '',
      data_fim: agendamento.data_fim?.split('T')[0] || ''
    });
    if (agendamento.configuracao_relatorio?.dias_pendente) {
      setConfigPedidosPendentes(agendamento.configuracao_relatorio);
    }
    setModalAberto(true);
  };

  // Fechar modal
  const fecharModal = () => {
    setModalAberto(false);
    setAgendamentoEditando(null);
  };

  // Salvar agendamento
  const salvarAgendamento = async (e) => {
    e.preventDefault();
    
    try {
      setLoadingAction(true);

      // Montar configuração do relatório
      let configuracao = {};
      if (formData.tipo_relatorio === 'pedidos_pendentes') {
        configuracao = configPedidosPendentes;
      }

      const dados = {
        ...formData,
        configuracao_relatorio: configuracao
      };

      // Converter valores vazios para null
      if (!dados.destinatario_usuario_id) dados.destinatario_usuario_id = null;
      if (!dados.dia_semana && dados.dia_semana !== 0) dados.dia_semana = null;
      if (!dados.dia_mes) dados.dia_mes = null;
      if (!dados.data_inicio) dados.data_inicio = null;
      if (!dados.data_fim) dados.data_fim = null;

      let response;
      if (agendamentoEditando) {
        response = await agendamentoWhatsAppService.atualizar(agendamentoEditando.id, dados);
      } else {
        response = await agendamentoWhatsAppService.criar(dados);
      }

      if (response.success) {
        showToast(agendamentoEditando ? 'Agendamento atualizado!' : 'Agendamento criado!', 'success');
        fecharModal();
        carregarAgendamentos();
      } else {
        showToast(response.message || 'Erro ao salvar', 'error');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      showToast(error.response?.data?.message || 'Erro ao salvar agendamento', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  // Deletar agendamento
  const deletarAgendamento = async (agendamento) => {
    if (!window.confirm(`Deseja realmente excluir o agendamento "${agendamento.nome}"?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      const response = await agendamentoWhatsAppService.deletar(agendamento.id);
      if (response.success) {
        showToast('Agendamento excluído!', 'success');
        carregarAgendamentos();
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
      showToast('Erro ao excluir agendamento', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  // Pausar/Retomar agendamento
  const toggleStatusAgendamento = async (agendamento) => {
    try {
      setLoadingAction(true);
      const response = await agendamentoWhatsAppService.toggleStatus(agendamento.id);
      if (response.success) {
        showToast(response.message, 'success');
        carregarAgendamentos();
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      showToast('Erro ao alterar status', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  // Executar manualmente
  const executarManualmente = async (agendamento) => {
    if (!window.confirm(`Deseja enviar a notificação "${agendamento.nome}" agora?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      const response = await agendamentoWhatsAppService.executarManualmente(agendamento.id);
      if (response.success) {
        showToast('Mensagem enviada com sucesso!', 'success');
        carregarAgendamentos();
      } else {
        showToast(response.message || 'Erro ao enviar', 'error');
      }
    } catch (error) {
      console.error('Erro ao executar:', error);
      showToast(error.response?.data?.message || 'Erro ao enviar mensagem', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  // Abrir histórico
  const abrirHistorico = async (agendamento) => {
    setHistoricoAgendamento(agendamento);
    setModalHistoricoAberto(true);
    setHistoricoLoading(true);

    try {
      const response = await agendamentoWhatsAppService.buscarHistorico(agendamento.id);
      if (response.success) {
        setHistorico(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      showToast('Erro ao carregar histórico', 'error');
    } finally {
      setHistoricoLoading(false);
    }
  };

  // Gerar preview
  const gerarPreview = async () => {
    try {
      setPreviewLoading(true);
      
      let configuracao = {};
      if (formData.tipo_relatorio === 'pedidos_pendentes') {
        configuracao = configPedidosPendentes;
      }

      const response = await agendamentoWhatsAppService.previewRelatorio(
        formData.tipo_relatorio,
        configuracao
      );
      
      if (response.success) {
        setPreviewData(response.data);
        setModalPreviewAberto(true);
      }
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      showToast('Erro ao gerar preview', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Paginação
  const irParaPagina = (novaPagina) => {
    setPagination(prev => ({ ...prev, page: novaPagina }));
  };

  // Formatar data/hora
  const formatarDataHora = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR');
  };

  // Formatar horário
  const formatarHorario = (horario) => {
    if (!horario) return '-';
    return horario.substring(0, 5);
  };

  // Renderizar configuração específica do relatório
  const renderizarConfigRelatorio = () => {
    switch (formData.tipo_relatorio) {
      case 'pedidos_pendentes':
        return (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-3">Configuração: Pedidos Pendentes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dias mínimos pendente
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={configPedidosPendentes.dias_pendente}
                  onChange={(e) => setConfigPedidosPendentes(prev => ({
                    ...prev,
                    dias_pendente: parseInt(e.target.value) || 5
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de referência
                </label>
                <input
                  type="date"
                  value={configPedidosPendentes.data_referencia}
                  onChange={(e) => setConfigPedidosPendentes(prev => ({
                    ...prev,
                    data_referencia: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      {/* Toast */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <FaWhatsapp className="text-2xl text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Agendamentos WhatsApp
              </h1>
              <p className="text-sm text-gray-500">
                Gerencie os agendamentos de notificações gerenciais via WhatsApp
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status WhatsApp */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              whatsappStatus?.connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              <FaWhatsapp />
              <span className="text-sm font-medium">
                {whatsappStatus?.connected ? 'Conectado' : 'Desconectado'}
              </span>
            </div>

            {/* Botão Atualizar */}
            <button
              onClick={() => { carregarAgendamentos(); verificarWhatsApp(); }}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>

            {/* Botão Novo */}
            <button
              onClick={abrirModalCriar}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
              Novo Agendamento
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Buscar por nome..."
                value={filtros.search}
                onChange={(e) => setFiltros(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <select
            value={filtros.status}
            onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="pausado">Pausado</option>
            <option value="erro">Com erro</option>
          </select>

          <select
            value={filtros.tipo_relatorio}
            onChange={(e) => setFiltros(prev => ({ ...prev, tipo_relatorio: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Todos os tipos</option>
            {tiposRelatorio.map(tipo => (
              <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
            ))}
          </select>

          {(filtros.search || filtros.status || filtros.tipo_relatorio) && (
            <button
              onClick={() => setFiltros({ status: '', tipo_relatorio: '', search: '' })}
              className="px-3 py-2 text-gray-600 hover:text-gray-800"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-1" />
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Lista de Agendamentos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="text-3xl text-green-600 animate-spin" />
          </div>
        ) : agendamentos.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faBell} className="text-4xl text-gray-300 mb-3" />
            <p className="text-gray-500">Nenhum agendamento encontrado</p>
            <button
              onClick={abrirModalCriar}
              className="mt-4 text-green-600 hover:text-green-700"
            >
              Criar primeiro agendamento
            </button>
          </div>
        ) : (
          <>
            {/* Tabela */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Agendamento
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Destinatário
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Recorrência
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Próximo Disparo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Disparos
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agendamentos.map((agendamento) => {
                    const statusConfig = CORES_STATUS[agendamento.status] || CORES_STATUS.ativo;
                    
                    return (
                      <tr key={agendamento.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{agendamento.nome}</div>
                          {agendamento.descricao && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {agendamento.descricao}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            <FontAwesomeIcon icon={faChartBar} />
                            {NOMES_TIPOS_RELATORIO[agendamento.tipo_relatorio] || agendamento.tipo_relatorio}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faUsers} className="text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {agendamento.destinatario_nome || agendamento.destinatario_usuario_nome || '-'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {agendamento.destinatario_telefone || agendamento.destinatario_usuario_celular || '-'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faClock} className="text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 capitalize">
                                {agendamento.recorrencia}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatarHorario(agendamento.horario)}
                                {agendamento.dia_semana !== null && ` • ${NOMES_DIAS_SEMANA[agendamento.dia_semana]}`}
                                {agendamento.dia_mes && ` • Dia ${agendamento.dia_mes}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {formatarDataHora(agendamento.proximo_disparo)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusConfig.bg} ${statusConfig.text}`}>
                            <FontAwesomeIcon icon={statusConfig.icon} />
                            <span className="capitalize">{agendamento.status}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <span className="text-green-600">{agendamento.disparos_sucesso}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-red-600">{agendamento.disparos_erro}</span>
                            <span className="text-gray-400"> ({agendamento.total_disparos})</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {/* Enviar Agora */}
                            <button
                              onClick={() => executarManualmente(agendamento)}
                              disabled={loadingAction}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Enviar Agora"
                            >
                              <FontAwesomeIcon icon={faPaperPlane} />
                            </button>

                            {/* Pausar/Retomar */}
                            <button
                              onClick={() => toggleStatusAgendamento(agendamento)}
                              disabled={loadingAction}
                              className={`p-2 rounded-lg transition-colors ${
                                agendamento.status === 'ativo' 
                                  ? 'text-yellow-600 hover:bg-yellow-50' 
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={agendamento.status === 'ativo' ? 'Pausar' : 'Retomar'}
                            >
                              <FontAwesomeIcon icon={agendamento.status === 'ativo' ? faPause : faPlay} />
                            </button>

                            {/* Histórico */}
                            <button
                              onClick={() => abrirHistorico(agendamento)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Ver Histórico"
                            >
                              <FontAwesomeIcon icon={faHistory} />
                            </button>

                            {/* Editar */}
                            <button
                              onClick={() => abrirModalEditar(agendamento)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>

                            {/* Deletar */}
                            <button
                              onClick={() => deletarAgendamento(agendamento)}
                              disabled={loadingAction}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => irParaPagina(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <span className="px-3 py-1 bg-gray-100 rounded-lg text-sm">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => irParaPagina(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">
                  {agendamentoEditando ? 'Editar Agendamento' : 'Novo Agendamento'}
                </h2>
                <button
                  onClick={fecharModal}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <form onSubmit={salvarAgendamento} className="p-6 space-y-4">
              {/* Nome e Descrição */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="Ex: Alerta de Pedidos Pendentes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Relatório *
                  </label>
                  <select
                    required
                    value={formData.tipo_relatorio}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipo_relatorio: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  >
                    {tiposRelatorio.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  rows={2}
                  placeholder="Descrição do agendamento..."
                />
              </div>

              {/* Configuração específica do relatório */}
              {renderizarConfigRelatorio()}

              {/* Destinatário */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-700 mb-3">Destinatário</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usuário do Sistema
                    </label>
                    <select
                      value={formData.destinatario_usuario_id}
                      onChange={(e) => {
                        const userId = e.target.value;
                        const usuario = usuarios.find(u => u.id === parseInt(userId));
                        setFormData(prev => ({
                          ...prev,
                          destinatario_usuario_id: userId,
                          destinatario_nome: usuario?.nome || prev.destinatario_nome,
                          destinatario_telefone: usuario?.celular 
                            ? formatarTelefoneWhatsApp(usuario.celular) 
                            : prev.destinatario_telefone
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Selecione um usuário (opcional)</option>
                      {usuarios.map(usuario => (
                        <option key={usuario.id} value={usuario.id}>
                          {usuario.nome} {usuario.celular ? `(${usuario.celular})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Destinatário
                    </label>
                    <input
                      type="text"
                      value={formData.destinatario_nome}
                      onChange={(e) => setFormData(prev => ({ ...prev, destinatario_nome: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="Nome do destinatário"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone WhatsApp *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.destinatario_telefone}
                    onChange={(e) => setFormData(prev => ({ ...prev, destinatario_telefone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="5521999999999"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formato: código do país + DDD + número (ex: 5521999999999)
                  </p>
                </div>
              </div>

              {/* Recorrência */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-700 mb-3">Recorrência</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequência *
                    </label>
                    <select
                      required
                      value={formData.recorrencia}
                      onChange={(e) => setFormData(prev => ({ ...prev, recorrencia: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    >
                      {recorrencias.map(rec => (
                        <option key={rec.id} value={rec.id}>{rec.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Horário *
                    </label>
                    <input
                      type="time"
                      required
                      value={formData.horario}
                      onChange={(e) => setFormData(prev => ({ ...prev, horario: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  {formData.recorrencia === 'semanal' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dia da Semana
                      </label>
                      <select
                        value={formData.dia_semana}
                        onChange={(e) => setFormData(prev => ({ ...prev, dia_semana: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Selecione</option>
                        {diasSemana.map(dia => (
                          <option key={dia.id} value={dia.id}>{dia.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {formData.recorrencia === 'mensal' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dia do Mês
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.dia_mes}
                        onChange={(e) => setFormData(prev => ({ ...prev, dia_mes: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="1-31"
                      />
                    </div>
                  )}
                </div>

                {/* Período */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Início (opcional)
                    </label>
                    <input
                      type="date"
                      value={formData.data_inicio}
                      onChange={(e) => setFormData(prev => ({ ...prev, data_inicio: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Fim (opcional)
                    </label>
                    <input
                      type="date"
                      value={formData.data_fim}
                      onChange={(e) => setFormData(prev => ({ ...prev, data_fim: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  type="button"
                  onClick={gerarPreview}
                  disabled={previewLoading}
                  className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  {previewLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faEye} />
                  )}
                  Preview do Relatório
                </button>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={fecharModal}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loadingAction}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {loadingAction && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                    {agendamentoEditando ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Histórico */}
      {modalHistoricoAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Histórico de Disparos
                  </h2>
                  {historicoAgendamento && (
                    <p className="text-sm text-gray-500">{historicoAgendamento.nome}</p>
                  )}
                </div>
                <button
                  onClick={() => setModalHistoricoAberto(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {historicoLoading ? (
                <div className="flex items-center justify-center py-12">
                  <FontAwesomeIcon icon={faSpinner} className="text-3xl text-green-600 animate-spin" />
                </div>
              ) : historico.length === 0 ? (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faHistory} className="text-4xl text-gray-300 mb-3" />
                  <p className="text-gray-500">Nenhum disparo realizado ainda</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historico.map((item) => (
                    <div
                      key={item.id}
                      className={`p-4 rounded-lg border ${
                        item.status === 'sucesso' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon
                            icon={item.status === 'sucesso' ? faCheckCircle : faTimesCircle}
                            className={item.status === 'sucesso' ? 'text-green-600' : 'text-red-600'}
                          />
                          <span className="font-medium text-gray-800">
                            {formatarDataHora(item.data_disparo)}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {item.tempo_execucao_ms}ms
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        <p><strong>Destino:</strong> {item.telefone_destino}</p>
                        {item.erro && (
                          <p className="text-red-600"><strong>Erro:</strong> {item.erro}</p>
                        )}
                      </div>
                      {item.mensagem && (
                        <details className="mt-2">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                            Ver mensagem enviada
                          </summary>
                          <pre className="mt-2 p-3 bg-white rounded border text-xs whitespace-pre-wrap">
                            {item.mensagem}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preview */}
      {modalPreviewAberto && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">
                  Preview do Relatório
                </h2>
                <button
                  onClick={() => setModalPreviewAberto(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  <FaWhatsapp />
                  Mensagem que será enviada
                </span>
              </div>
              
              <div className="bg-gray-100 p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                  {previewData.mensagem}
                </pre>
              </div>

              {previewData.dados && (
                <details className="mt-4">
                  <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                    Ver dados do relatório (JSON)
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded border text-xs overflow-auto max-h-60">
                    {JSON.stringify(previewData.dados, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendamentosWhatsAppPage;
